// client/src/components/ManagersTab.jsx
// "מנהלים" tab on CampDetailPage. Lists current camp managers + super-
// admin-only invite/remove controls.
//
// Note: a camp manager is whoever can see the back office for this camp —
// not every teacher gets this access (granular role permissions will land
// in a later wave). The teacher-with-EARN-device role keeps the operational
// label "מורה" wherever it appears on devices / login marketing copy.
//
// Managers themselves can VIEW the list (handy "who else manages this
// camp?") but cannot invite/remove — that's super-admin only, matching the
// /api/camps/:campId/managers route gates from wave 2 sub-task 5.

import { useMemo, useRef, useState } from 'react';
import ListToolbar from './ListToolbar.jsx';
import { matchesText, sortRows } from '../lib/listTools.js';
import {
  useManagers,
  useInviteManager,
  useRemoveManager,
} from '../hooks/useManagers.js';
import Spinner from './Spinner.jsx';
import { ApiError } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { UserGroupIcon } from './Icons.jsx';

export default function ManagersTab({ user, campId }) {
  const managers = useManagers(campId);
  const inviteMutation = useInviteManager(campId);
  const removeMutation = useRemoveManager(campId);

  const [email, setEmail] = useState('');
  const [inviteError, setInviteError] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  async function handleInvite(e) {
    e.preventDefault();
    setInviteError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    try {
      await inviteMutation.mutateAsync(trimmed);
      setEmail('');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'conflict') setInviteError('המנהל כבר רשום בתכנית.');
        else if (err.status === 422) setInviteError('כתובת אימייל לא תקינה.');
        else if (err.status === 404) setInviteError('התכנית לא נמצאה.');
        else setInviteError(err.message || 'שגיאה לא צפויה.');
      } else {
        setInviteError('שגיאה לא צפויה.');
      }
    }
  }

  async function handleRemove(userId) {
    try {
      await removeMutation.mutateAsync(userId);
      setRemoveTarget(null);
    } catch {
      /* error shown via RemoveConfirm closure */
    }
  }

  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const all = managers.data ?? [];
  const shown = useMemo(
    () =>
      sortRows(
        all.filter((m) => matchesText(m, q, ['name', 'email'])),
        sort,
        {
          name: (m) => m.name || m.email,
          email: (m) => m.email,
          createdAt: (m) => (m.createdAt ? new Date(m.createdAt) : null),
        }
      ),
    [all, q, sort]
  );

  if (managers.isLoading) return <Spinner label="טוען מנהלים..." />;
  if (managers.isError) {
    return (
      <div className="card-danger p-4 text-joy-900 text-sm">
        שגיאה: {managers.error.message}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {user.isSuperAdmin && (
        <div className="card-soft p-5">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-brand-100 text-brand-700 grid place-items-center shrink-0">
              <UserGroupIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="h-section">הזמנת מנהל חדש</h2>
              <p className="text-sm text-ink-600 mt-1">
                הזן אימייל. אם המנהל עוד לא נכנס דרך Google — תיווצר הזמנה ממתינה,
                והפרטים יתמלאו בכניסה הראשונה. למנהל תהיה גישה לכל לשוניות התכנית.
              </p>
              <form onSubmit={handleInvite} className="mt-3 flex gap-2 items-start">
                <div className="flex-1">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    dir="ltr"
                  />
                  {inviteError && (
                    <div className="field-error">{inviteError}</div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={inviteMutation.isPending}
                >
                  {inviteMutation.isPending ? 'מזמין...' : 'הזמן'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {all.length > 1 && (
        <ListToolbar
          search={{ value: q, onChange: setQ, placeholder: 'חיפוש לפי שם או אימייל...' }}
          sort={{
            value: sort,
            onChange: setSort,
            options: [
              { key: 'name', label: 'שם' },
              { key: 'email', label: 'אימייל' },
              { key: 'createdAt', label: 'תאריך הוספה' },
            ],
          }}
          count={{ shown: shown.length, total: all.length, noun: 'מנהלים' }}
        />
      )}

      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
          <h2 className="h-section">מנהלים פעילים</h2>
          <span className="badge-slate num">{all.length}</span>
        </div>
        {all.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-3xl bg-brand-100 text-brand-700 grid place-items-center mb-3">
              <UserGroupIcon className="w-7 h-7" />
            </div>
            <p className="text-sm text-ink-500">אין עדיין מנהלים לתכנית זו.</p>
          </div>
        ) : (
          <ul className="divide-y divide-ink-100">
            {shown.map((m) => (
              <ManagerRow
                key={m.userId}
                manager={m}
                canRemove={user.isSuperAdmin}
                onRemove={() =>
                  setRemoveTarget({ userId: m.userId, name: m.name || m.email })
                }
              />
            ))}
          </ul>
        )}
      </div>

      <RemoveConfirm
        target={removeTarget}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => handleRemove(removeTarget.userId)}
        pending={removeMutation.isPending}
      />
    </div>
  );
}

function ManagerRow({ manager: m, canRemove, onRemove }) {
  const displayName = m.name || m.email.split('@')[0];
  const initial = (m.name || m.email)[0]?.toUpperCase() || '·';
  return (
    <li className="px-5 py-3 flex items-center gap-3 hover:bg-ink-50/60 transition-colors">
      {m.picture ? (
        <img
          src={m.picture}
          alt=""
          className="h-10 w-10 rounded-full bg-ink-200 object-cover"
        />
      ) : (
        <div className="h-10 w-10 rounded-full bg-brand-gradient text-white grid place-items-center font-bold text-sm">
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-ink-900 truncate">{displayName}</span>
          {m.isShell && <span className="badge-clay">ממתין לכניסה ראשונה</span>}
          {m.isSuperAdmin && <span className="badge-indigo">Super Admin</span>}
        </div>
        <div className="text-xs text-ink-500" dir="ltr">{m.email}</div>
      </div>
      <div className="text-xs text-ink-400 hidden sm:block num">
        הוזמן {formatDate(m.createdAt)}
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="btn-sm text-joy-700 hover:bg-joy-50 rounded-lg px-3 py-1.5 font-semibold transition-colors"
        >
          הסר
        </button>
      )}
    </li>
  );
}

function RemoveConfirm({ target, onCancel, onConfirm, pending }) {
  const downOnBackdropRef = useRef(false);
  if (!target) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in"
      role="presentation"
      onMouseDown={(e) => { downOnBackdropRef.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => {
        // Same drag-out-of-modal fix as the shared Modal — only close if
        // down + up were both on the backdrop, otherwise a text-select drag
        // out of the dialog would dismiss it.
        if (downOnBackdropRef.current && e.target === e.currentTarget) onCancel();
        downOnBackdropRef.current = false;
      }}
    >
      <div className="card-pop w-full max-w-md p-6 animate-slide-up" role="dialog" aria-modal="true">
        <h2 className="h-section">הסרת מנהל</h2>
        <p className="text-sm text-ink-700 mt-2">
          להסיר את <strong>{target.name}</strong> מהתכנית? לא יהיה אפשר יותר
          לגשת לתכנית מטעמו. ההיסטוריה נשמרת ב-DB (soft-delete).
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            ביטול
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'מסיר...' : 'כן, הסר'}
          </button>
        </div>
      </div>
    </div>
  );
}
